import type { FC, MutableRefObject, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import { LOGIN_USER_EMAIL, SIGNUP_EMAIL_VERIFICATION_ENABLED } from "@/configs";
import { PATH } from "@/configs/router/routerConfig";
import SidebarLayoutSkeleton from "@/layouts/SidebarLayoutSkeleton";
import {
  attemptSessionRefresh,
  cancelScheduledSessionRefresh,
  getSessionExpiryDetails,
  scheduleProactiveSessionRefresh
} from "@/lib/auth/sessionRefresh";
import {
  buildUserFromSessionPayload,
  isEmailVerificationPending,
  shouldHydrateSessionUser
} from "@/lib/auth/sessionUser";
import { ensureSuperTokensInitialized } from "@/lib/auth/supertokensInit";
import { logger } from "@/lib/logger";
import { buildSignupVerificationPath, normalizeSignupBackTo } from "@/lib/signup/setupRouting";
import { login as loginAction } from "@/slices/auth/authReducer";
import { logOutUser } from "@/slices/auth/authThunk";
import { hasPendingLogoutRedirectTarget } from "@/slices/auth/logoutRedirect";
import { useDispatch, useSelector } from "@/store";
import { rootReducerName } from "@/store/rootReducer";
import { getCookie } from "@/utils/session";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import Session from "supertokens-auth-react/recipe/session";

interface AuthenticatedProps {
  children: ReactNode;
  authRequire: boolean;
}

async function redirectAuthEntrySession(router: ReturnType<typeof useRouter>, pathname: string): Promise<void> {
  const loginUserEmail = getCookie(LOGIN_USER_EMAIL);
  if (!loginUserEmail) {
    return;
  }

  try {
    const payload = await Session.getAccessTokenPayloadSecurely();
    if (pathname === PATH.SIGN_UP && SIGNUP_EMAIL_VERIFICATION_ENABLED && isEmailVerificationPending(payload)) {
      const invitationCode = router.query.invitationcode ?? router.query.invitationCode ?? router.query.invitation_code;
      void router.replace(buildSignupVerificationPath(normalizeSignupBackTo(router.query.backTo), invitationCode));
      return;
    }
  } catch {
    // Keep the existing auth-entry redirect for stale cookies or unavailable session state.
  }

  void router.push(PATH.HOME_PAGE);
}

async function completeSessionBootstrap(params: {
  dispatch: ReturnType<typeof useDispatch>;
  routerPathname: string;
  currentUser: Record<string, unknown> | null | undefined;
  isCancelled: () => boolean;
  payload: Record<string, unknown> | null | undefined;
  setSessionBootstrapReady: (value: boolean) => void;
}): Promise<boolean> {
  const { currentUser, dispatch, isCancelled, payload, routerPathname, setSessionBootstrapReady } = params;
  const hydratedUser = buildUserFromSessionPayload(payload, getCookie(LOGIN_USER_EMAIL) || "");

  if (!hydratedUser) {
    logger.warn("[Authenticated] Session exists but could not hydrate user", {
      pathname: routerPathname
    });
    return false;
  }

  if (shouldHydrateSessionUser(currentUser, hydratedUser)) {
    dispatch(
      loginAction({
        user: hydratedUser,
        role: Number(hydratedUser.role ?? 0)
      })
    );
  }

  if (isCancelled()) {
    return false;
  }

  setSessionBootstrapReady(true);
  return true;
}

async function loadSessionBootstrap(params: {
  dispatch: ReturnType<typeof useDispatch>;
  routerPathname: string;
  currentUser: Record<string, unknown> | null | undefined;
  isCancelled: () => boolean;
  setSessionBootstrapReady: (value: boolean) => void;
}): Promise<boolean> {
  const { dispatch, isCancelled, routerPathname, setSessionBootstrapReady } = params;

  ensureSuperTokensInitialized();
  try {
    const payload = await Session.getAccessTokenPayloadSecurely();
    return completeSessionBootstrap({
      currentUser: params.currentUser,
      dispatch,
      isCancelled,
      payload,
      routerPathname,
      setSessionBootstrapReady
    });
  } catch (err) {
    logger.error("[Authenticated] Failed to hydrate auth user from session payload", { err });
    if (!isCancelled()) {
      setSessionBootstrapReady(true);
    }
    return false;
  }
}

function bootstrapAuthenticatedSession(params: {
  dispatch: ReturnType<typeof useDispatch>;
  logoutRequestedRef: MutableRefObject<boolean>;
  routerPathname: string;
  currentUser: MutableRefObject<Record<string, unknown> | null | undefined>;
  isCancelled: () => boolean;
  setSessionBootstrapReady: (value: boolean) => void;
  triggerLogout: (reason: string) => void;
}): void {
  const {
    currentUser,
    dispatch,
    isCancelled,
    logoutRequestedRef,
    routerPathname,
    setSessionBootstrapReady,
    triggerLogout
  } = params;

  ensureSuperTokensInitialized();
  void Session.doesSessionExist()
    .then(async (sessionExists) => {
      if (isCancelled()) return;

      if (!sessionExists) {
        logger.debug("[Authenticated] Session does not exist, attempting refresh", { pathname: routerPathname });

        void attemptSessionRefresh("Authenticated")
          .then(async (refreshed) => {
            if (isCancelled()) return;

            if (refreshed) {
              logoutRequestedRef.current = false;
              const hydrated = await loadSessionBootstrap({
                currentUser: currentUser.current,
                dispatch,
                isCancelled,
                routerPathname,
                setSessionBootstrapReady
              });
              if (hydrated) {
                return;
              }
              if (isCancelled()) return;

              const retried = await loadSessionBootstrap({
                currentUser: currentUser.current,
                dispatch,
                isCancelled,
                routerPathname,
                setSessionBootstrapReady
              });
              if (retried) {
                logoutRequestedRef.current = false;
                return;
              }
              triggerLogout("session payload missing user identity after refresh attempt");
              return;
            }

            logger.debug("session missing, redirecting to sign-in through logout flow");
            triggerLogout("session missing after refresh attempt");
          })
          .catch((err) => {
            logger.error("Supertoken session refresh error:", err);
            if (!isCancelled()) {
              triggerLogout("session refresh error");
            }
          });
        return;
      }

      const hydratedFromPayload = await loadSessionBootstrap({
        currentUser: currentUser.current,
        dispatch,
        isCancelled,
        routerPathname,
        setSessionBootstrapReady
      });
      if (hydratedFromPayload) {
        logoutRequestedRef.current = false;
        return;
      }

      triggerLogout("session payload missing user identity");
    })
    .catch((err) => {
      logger.error("Authenticated: failed to check session existence", { err });
      if (!isCancelled()) {
        logger.debug("[Authenticated] Falling back to session refresh after failed session existence check", {
          pathname: routerPathname
        });
        void attemptSessionRefresh("Authenticated")
          .then((refreshed) => {
            if (isCancelled()) return;

            if (refreshed) {
              logoutRequestedRef.current = false;
              void loadSessionBootstrap({
                currentUser: currentUser.current,
                dispatch,
                isCancelled,
                routerPathname,
                setSessionBootstrapReady
              });
              return;
            }

            triggerLogout("session existence check failed");
          })
          .catch((refreshErr) => {
            logger.error("Supertoken session refresh error:", refreshErr);
            if (!isCancelled()) {
              triggerLogout("session existence refresh error");
            }
          });
      }
    });
}

function useAuthenticatedSessionBootstrap(params: {
  authRequire: boolean;
  dispatch: ReturnType<typeof useDispatch>;
  logoutRequestedRef: MutableRefObject<boolean>;
  router: ReturnType<typeof useRouter>;
  user: unknown;
  setSessionBootstrapReady: (value: boolean) => void;
}) {
  const { authRequire, dispatch, logoutRequestedRef, router, user, setSessionBootstrapReady } = params;
  const userRef = useRef<Record<string, unknown> | null | undefined>(
    user as Record<string, unknown> | null | undefined
  );

  useEffect(() => {
    userRef.current = user as Record<string, unknown> | null | undefined;
  }, [user]);

  const triggerLogout = (reason: string) => {
    if (logoutRequestedRef.current) {
      return;
    }

    if (hasPendingLogoutRedirectTarget()) {
      logoutRequestedRef.current = true;
      logger.debug("[Authenticated] Logout already pending, skipping duplicate logout dispatch", {
        pathname: router.asPath,
        reason
      });
      return;
    }

    logoutRequestedRef.current = true;
    dispatch(logOutUser({}));
  };

  useEffect(() => {
    let cancelled = false;
    let cancelProactiveRefresh: (() => void) | null = null;
    const routerPathname = router.asPath;
    const isCancelled = () => cancelled;

    if (authRequire) {
      // RouteGuard already verifies access for every client-side navigation.
      // Keep the existing shell mounted while this background session check
      // runs so side-menu navigation does not replace the page with a loader.
      if (!userRef.current) {
        setSessionBootstrapReady(false);
      }
      logger.debug("[Authenticated] Checking session existence", { pathname: routerPathname });

      void getSessionExpiryDetails().then((details) => {
        if (cancelled) return;

        if (details.expiryMs) {
          logger.debug("[Authenticated] Session expiry found, scheduling proactive refresh", {
            pathname: routerPathname,
            expirySource: details.source,
            expiryMs: details.expiryMs,
            msUntilExpiry: details.msUntilExpiry
          });
          cancelProactiveRefresh = scheduleProactiveSessionRefresh(
            { tokenExpiryMs: details.expiryMs },
            { source: "Authenticated" }
          );
        } else {
          logger.debug("[Authenticated] No usable session expiry found", {
            pathname: routerPathname,
            expirySource: details.source
          });
          cancelScheduledSessionRefresh();
        }
      });

      bootstrapAuthenticatedSession({
        currentUser: userRef,
        dispatch,
        isCancelled,
        logoutRequestedRef,
        routerPathname,
        setSessionBootstrapReady,
        triggerLogout
      });
    } else {
      setSessionBootstrapReady(true);
      if ([PATH.SIGN_UP, PATH.LOGIN].includes(window.location.pathname)) {
        void redirectAuthEntrySession(router, window.location.pathname);
      }
    }

    return () => {
      cancelled = true;
      cancelProactiveRefresh?.();
    };
  }, [authRequire, dispatch]);
}

export const Authenticated: FC<AuthenticatedProps> = (props) => {
  const { children, authRequire } = props;
  const router = useRouter();
  const dispatch = useDispatch();
  const user = useSelector((state) => state[rootReducerName.auth].user);
  const loading = useSelector((state) => state[rootReducerName.auth].loading);
  const logoutRequestedRef = useRef(false);
  const [sessionBootstrapReady, setSessionBootstrapReady] = useState(() => !authRequire || Boolean(user));

  useAuthenticatedSessionBootstrap({
    authRequire,
    dispatch,
    logoutRequestedRef,
    router,
    user,
    setSessionBootstrapReady
  });

  // Block rendering until auth is ready
  if (authRequire && (loading || !user || !sessionBootstrapReady)) {
    return <SidebarLayoutSkeleton message="Authenticated: session check/loading" />;
  }

  return <>{children}</>;
};

Authenticated.propTypes = {
  children: PropTypes.node
};
